import React from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { Formik, Form } from 'formik';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';
import { useQuery, useMutation } from '@apollo/client';
import Alert from 'react-bootstrap/Alert';
import { queries, mutations, useTeamPermissions, graphqlErrorsToUiErrors } from '../api';
import { useNotifications } from '../notifications';
import { useTeamInfo } from '../team-id-context';
import { TextField } from '../components/fields';
import SubmitButton from '../components/submit-button';
import ErrorMessage from '../components/error-message';
import { EditPermissionsFormFragment } from '../components/editPermissionsList/editPermissionsFormFragment';
import LoadingSpinner from '../components/loading-spinner';
import { Permissions } from '../shared/constants/permissions';
import { useAuth } from '../auth';
import { InviteNewTeamMembersByEmailMutationVariables } from '../gql/graphql';

interface AddTeamMemberFormProps {
  bodyContainer?: any;
  actionsContainer?: any;
  renderSecondaryAction: any;
  errors: { [key: string]: any };
  isSubmitting: boolean;
  isValid: boolean;
}

const AddTeamMemberForm = ({
  bodyContainer: BodyContainer = React.Fragment,
  actionsContainer: ActionsContainer = React.Fragment,
  renderSecondaryAction,
  errors,
  isSubmitting,
  isValid,
}: AddTeamMemberFormProps) => (
  <Form noValidate style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <BodyContainer>
      {errors._general && (
        <Alert variant="danger">
          <ErrorMessage>{errors._general}</ErrorMessage>
        </Alert>
      )}

      <h3>
        <FormattedMessage id="title.email-address" defaultMessage="Email address" />
      </h3>
      <span>
        <FormattedMessage
          id="phrase.new-team-member-will-receive-activation-email"
          defaultMessage="Your new team member will receive an email to activate their account."
        />
      </span>
      <Row>
        <Col className="mb-1" lg={7}>
          <TextField id="addTeamMemberForm.email" name="email" type="email" />
        </Col>
      </Row>
      <h3>
        <FormattedMessage id="title.permissions" defaultMessage="Permissions" />
      </h3>
      <p>
        <FormattedMessage
          id="phrase.view-or-change-team-members-permissions"
          defaultMessage="View or change this team member’s permissions"
        />
      </p>
      <EditPermissionsFormFragment userPermissions={{}} />
    </BodyContainer>

    <ActionsContainer>
      {renderSecondaryAction && renderSecondaryAction()}
      <SubmitButton
        label={<FormattedMessage id="cta.send-invitation-email" defaultMessage="Send invitation email" />}
        disabled={isSubmitting || !isValid}
        isSubmitting={isSubmitting}
      />
    </ActionsContainer>
  </Form>
);

const schema = yup.object().shape({
  teamId: yup.string().required(),
  email: yup.string()
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      'Please enter a valid email address'
    )
    .required('Email is required'),
  defaultPermissions: yup.array().of(yup.string()).ensure().required(),
});

interface AddTeamMemberFormikProps {
  bodyContainer?: any;
  actionsContainer?: any;
  renderSecondaryAction?: (...args: any[]) => any;
  onSuccess?: (...args: any[]) => any;
}

const AddTeamMemberFormik = ({
  bodyContainer,
  actionsContainer,
  renderSecondaryAction,
  onSuccess,
}: AddTeamMemberFormikProps) => {
  const { loading: teamInfoLoading, teamId } = useTeamInfo();
  const { loading: permissionsLoading, teamPermissions } = useTeamPermissions();
  const [, { error: notifyError }] = useNotifications();
  const [inviteNewTeamMember] = useMutation(mutations.inviteNewTeamMembersByEmail);
  const [{ user }] = useAuth();

  // Query team members to check for existing emails
  const { data: teamData, loading: teamDataLoading } = useQuery(queries.getTeamById, {
    skip: !teamId,
    variables: { teamId },
  });

  const loading = teamInfoLoading || permissionsLoading || teamDataLoading;

  if (loading) {
    return <LoadingSpinner />;
  }

  const viewPermissionId = teamPermissions?.find(({ key }) => key === Permissions.View)?.id;

  const defaultPermissions = [];
  if (viewPermissionId != null) defaultPermissions.push(viewPermissionId);

  const onSubmit = async (values: { [key: string]: any }, actions: any) => {
    const { email, teamId, defaultPermissions } = values;
    
    // Check if email already exists in team
    const existingMembers = teamData?.teamById?.teamMembers || [];
    const pendingInvitations = teamData?.teamById?.pendingInvitations || [];
    
    const emailLowerCase = email.toLowerCase();
    const isExistingMember = existingMembers.some((member: any) => 
      member.user?.email?.toLowerCase() === emailLowerCase
    );
    const isPendingInvitation = pendingInvitations.some((invitation: any) => 
      invitation.email?.toLowerCase() === emailLowerCase
    );
    
    if (isExistingMember) {
      const errorMessage = 'This user is already a member of the team';
      actions.setErrors({ _general: errorMessage });
      return;
    }
    
    if (isPendingInvitation) {
      const errorMessage = 'An invitation has already been sent to this email address';
      actions.setErrors({ _general: errorMessage });
      return;
    }
    
    const variables: InviteNewTeamMembersByEmailMutationVariables = {
      emails: [email],
      teamId,
      defaultDeals: [],
      userPermissions: [
        {
          email: email,
          permissionIds: defaultPermissions,
        },
      ],
    };

    try {
      await inviteNewTeamMember({ variables });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const errors = graphqlErrorsToUiErrors(error);
      actions.setErrors(errors);
      
      // If no specific error message was set, add a generic one
      if (!errors._general) {
        actions.setErrors({ _general: error.message || 'Failed to send invitation' });
      }
    }
  };

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      validateOnMount={true}
      initialValues={{
        teamId,
        email: '',
        defaultPermissions,
      }}
    >
      {(formikProps) => (
        <AddTeamMemberForm
          {...formikProps}
          bodyContainer={bodyContainer}
          actionsContainer={actionsContainer}
          renderSecondaryAction={renderSecondaryAction}
        />
      )}
    </Formik>
  );
};

export default AddTeamMemberFormik;
